import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, ExternalLink, FileText, Globe, HardDrive, LayoutGrid, List, Loader2, Lock, Plus, RefreshCw, Settings, ShieldCheck, type LucideIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { WebsiteSummary } from "@hostdock/types";
import { ActionMenu, AppLogo, appTypeMeta, cn, ErrorState, formatBytes, formatRelative, runtimeLabel, Skeleton, toast, WebsiteStatusBadge } from "@hostdock/ui";
import { Cell, FilterTabs, InfoCard, InfoRow, PageHero, primaryButtonClass, SearchBox, StatCard, tableCell, Toolbar } from "../components/page-kit";
import { api } from "../api";
import { DOCS_HREF } from "../layout";

type View = "grid" | "list";
type Filter = "all" | "online" | "attention";
const VIEW_KEY = "hostdock.websites.view";
const readView = (): View => {
  try {
    return localStorage.getItem(VIEW_KEY) === "grid" ? "grid" : "list";
  } catch {
    return "list";
  }
};
const needsAttention = (w: WebsiteSummary) => ["FAILED", "SUSPENDED"].includes(w.status) || w.ssl === "FAILED" || w.ssl === "EXPIRED" || w.ssl === "EXPIRING";
/** "WooCommerce · PHP 8.5", or just "Node.js 24" when the app is the runtime itself. */
const appLine = (w: WebsiteSummary) => {
  const label = appTypeMeta(w.appType, w.runtime).label;
  const rt = runtimeLabel(w.runtime, w.runtimeVersion);
  return label.endsWith(" application") ? rt : `${label} · ${rt}`;
};
const siteUrl = (w: WebsiteSummary) => `${w.status === "ACTIVE" && w.ssl === "ISSUED" ? "https" : "http"}://${w.primaryDomain}`;
const externalDocs = /^https?:\/\//.test(DOCS_HREF);

/** Tinted circle behind each application's logo, as on the dashboard. */
const LOGO_TILE: Record<string, string> = { WOOCOMMERCE: "bg-[#fbe9f3]", WORDPRESS: "bg-[#e6eefa]", NODE: "bg-[#d9f7e4]", PHP: "bg-[#e8e9f7]", PYTHON: "bg-[#e5eef8]" };

/**
 * A flag only when the admin-defined region code starts with a real ISO country code ("US-UT", "BD"); otherwise none,
 * so nothing is guessed from a free-text region name.
 */
function flagOf(code: string): string | null {
  const cc = /^([A-Z]{2})(?:[-_]|$)/.exec(code)?.[1];
  if (!cc) return null;
  try {
    const name = new Intl.DisplayNames(["en"], { type: "region" }).of(cc);
    if (!name || name === cc || /unknown/i.test(name)) return null;
  } catch {
    return null;
  }
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export function WebsitesPage() {
  const navigate = useNavigate();
  const [view, setViewState] = useState<View>(readView);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // storage unavailable; the choice lasts for this page only
    }
  };
  const q = useQuery({ queryKey: ["websites"], queryFn: () => api.websites.list({ pageSize: 100 }), refetchInterval: (q) => (q.state.data?.items.some((w) => w.screenshotPending) ? 5_000 : 15_000) });
  const items = q.data?.items;
  const summary = useMemo(() => {
    if (!items) return null;
    const online = items.filter((w) => w.status === "ACTIVE").length;
    const ssl = items.filter((w) => w.ssl === "ISSUED").length;
    const measured = items.filter((w) => w.diskBytes != null);
    return {
      total: items.length,
      online,
      ssl,
      attention: items.filter(needsAttention).length,
      storage: measured.length ? measured.reduce((a, w) => a + (w.diskBytes ?? 0), 0) : null,
      onlinePct: items.length ? Math.round((online / items.length) * 100) : 0,
      sslPct: items.length ? Math.round((ssl / items.length) * 100) : 0,
    };
  }, [items]);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items?.filter((w) => (filter === "online" ? w.status === "ACTIVE" : filter === "attention" ? needsAttention(w) : true) && (!term || w.primaryDomain.toLowerCase().includes(term)));
  }, [items, filter, search]);

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={Globe}
        title="Websites"
        description="Manage your websites and applications."
        action={<Link to="/websites/new" className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />Create website</Link>}
      />

      {items && items.length > 0 && summary && (
        <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
          <StatCard icon={Globe} tile="teal" label="Total websites" value={summary.total} />
          <StatCard icon={Activity} tile="green" label="Online" value={summary.online} sub={`${summary.onlinePct}% running`} />
          <StatCard icon={ShieldCheck} tile="blue" label="SSL enabled" value={summary.ssl} sub={`${summary.sslPct}% secured`} />
          <StatCard icon={HardDrive} tile="orange" label="Total storage" value={summary.storage == null ? "Unavailable" : formatBytes(summary.storage)} sub={summary.storage == null ? "Not measured yet" : undefined} />
        </div>
      )}

      {items && items.length > 0 && summary && (
        <Toolbar>
          <FilterTabs<Filter> label="Filter websites" value={filter} onChange={setFilter} items={[["all", `All (${summary.total})`], ["online", `Online (${summary.online})`], ["attention", `Needs attention (${summary.attention})`]] as const} />
          <SearchBox value={search} onChange={setSearch} placeholder="Search websites..." />
          <div className="ml-auto flex gap-[4px] rounded-lg border border-line p-[3px]" role="group" aria-label="View">
            <ViewButton active={view === "grid"} label="Grid view" onClick={() => setView("grid")} icon={LayoutGrid} />
            <ViewButton active={view === "list"} label="Table view" onClick={() => setView("list")} icon={List} />
          </div>
        </Toolbar>
      )}

      <div className="mt-[15px]">
        {q.error ? (
          <div className="hd-card p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        ) : q.isPending ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : !items || items.length === 0 ? (
          <div className="hd-card flex flex-col items-center px-6 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent"><Globe size={24} /></span>
            <p className="mt-3 text-[14px] font-semibold">No websites yet</p>
            <p className="mt-1 text-[12.5px] text-muted">Create your first website in a few clicks.</p>
            <Link to="/websites/new" className="mt-4 inline-flex h-9 items-center gap-2 rounded-lg bg-[#048d9f] px-4 text-[12.5px] font-semibold text-white hover:bg-[#037d8d]"><Plus size={15} />Create website</Link>
          </div>
        ) : rows && rows.length === 0 ? (
          <div className="hd-card px-6 py-12 text-center">
            <p className="text-[13.5px] font-semibold">No matching websites</p>
            <p className="mt-1 text-[12.5px] text-muted">Try a different search or filter.</p>
          </div>
        ) : view === "list" ? (
          <WebsiteTable rows={rows ?? []} onOpen={(w) => navigate(`/websites/${w.id}`)} />
        ) : (
          <ul className="grid gap-[11px] md:grid-cols-2 xl:grid-cols-3">
            {rows?.map((w) => <WebsiteCard key={w.id} w={w} onOpen={() => navigate(`/websites/${w.id}`)} />)}
          </ul>
        )}
      </div>

      {items && items.length > 0 && summary && (
        <InfoRow>
          <InfoCard
            to="/domains"
            icon={Lock}
            tile="bg-[#d5f4f6] text-[#0a8ea0]"
            className="bg-[#eef9fb] dark:bg-surface-2"
            title="Keep your website secure"
            body={summary.ssl === summary.total
              ? `${summary.total === 1 ? "Your website has" : "All your websites have"} SSL enabled and ${summary.total === 1 ? "is" : "are"} securely protected. Keep your applications and plugins up to date to ensure the best security.`
              : `${summary.total - summary.ssl} of ${summary.total} websites don't have an active SSL certificate yet. Point their domains to HostDock and HTTPS is set up automatically.`}
          />
          {/* Only with a real documentation site: the in-app fallback is the developer (API) guide, not website help. */}
          {externalDocs && (
            <InfoCard
              to={DOCS_HREF}
              external
              icon={FileText}
              tile="bg-[#d9eafe] text-[#2f6fd6]"
              className="bg-[#eff5fc] dark:bg-surface-2"
              title="Need help with your website?"
              body="Learn how to manage your website, set up SSL, create backups and more in our documentation."
            />
          )}
        </InfoRow>
      )}
    </div>
  );
}

function ViewButton({ active, label, onClick, icon: Icon }: { active: boolean; label: string; onClick(): void; icon: LucideIcon }) {
  return (
    <button type="button" aria-pressed={active} aria-label={label} title={label} onClick={onClick} className={cn("flex h-[29px] w-[33px] items-center justify-center rounded-md transition-colors", active ? "bg-[#d9f5f9] text-[#0a8ea0] dark:bg-accent-soft dark:text-accent" : "text-fg/75 hover:bg-surface-2")}>
      <Icon size={16} />
    </button>
  );
}

const SSL_PILL: Record<WebsiteSummary["ssl"], { label: string; cls: string }> = {
  ISSUED: { label: "Secure", cls: "bg-[#d9f3e6] text-[#14a04a] dark:bg-success-soft" },
  PENDING: { label: "Pending", cls: "bg-warning-soft text-warning" },
  EXPIRING: { label: "Expiring", cls: "bg-warning-soft text-warning" },
  EXPIRED: { label: "Expired", cls: "bg-danger-soft text-danger" },
  FAILED: { label: "Failed", cls: "bg-danger-soft text-danger" },
  NONE: { label: "No SSL", cls: "bg-surface-3 text-muted" },
};

function SslPill({ ssl }: { ssl: WebsiteSummary["ssl"] }) {
  const p = SSL_PILL[ssl];
  return <span className={cn("inline-flex h-[27px] items-center gap-[7px] rounded-lg px-[11px] text-[12.5px] font-medium whitespace-nowrap", p.cls)}><Lock size={14} strokeWidth={2} />{p.label}</span>;
}

function StatusPill({ status }: { status: string }) {
  const ok = status === "ACTIVE";
  if (!ok) return <WebsiteStatusBadge status={status} />;
  return <span className="inline-flex h-[27px] items-center gap-[8px] rounded-full bg-[#e3f8ec] px-[11px] text-[12.5px] font-medium text-[#14a04a] dark:bg-success-soft"><span className="h-[7px] w-[7px] rounded-full bg-[#14a04a]" />Active</span>;
}

function Region({ w }: { w: WebsiteSummary }) {
  const flag = flagOf(w.region.code);
  return (
    <span className="flex max-w-full min-w-0 items-center gap-[9px]" title={w.region.name}>
      {flag && <span className="text-[18px] leading-none" aria-hidden>{flag}</span>}
      <span className="truncate">{w.region.name}</span>
    </span>
  );
}

function SiteLogo({ w, size = 48 }: { w: WebsiteSummary; size?: number }) {
  return (
    <span className={cn("flex shrink-0 items-center justify-center rounded-xl dark:bg-surface-3", LOGO_TILE[w.appType] ?? LOGO_TILE[w.runtime.toUpperCase()] ?? "bg-[#e3f5f8]")} style={{ width: size, height: size }}>
      <AppLogo appType={w.appType} runtime={w.runtime} size="lg" className="!bg-transparent !ring-0" />
    </span>
  );
}

/** Table on wide screens; each row turns into a stacked card below xl so nothing is clipped. */
function WebsiteTable({ rows, onOpen }: { rows: WebsiteSummary[]; onOpen(w: WebsiteSummary): void }) {
  // Columns share the width so the table fills wide screens; minimums keep everything readable at 1280px.
  const cols = "xl:grid xl:grid-cols-[minmax(220px,2.6fr)_minmax(130px,1.3fr)_minmax(150px,1.3fr)_minmax(80px,0.7fr)_minmax(110px,0.9fr)_minmax(100px,0.9fr)_180px] xl:items-center";
  const cell = cn(tableCell, "xl:pr-2");
  return (
    <div className="hd-card px-[18px] pb-[3px]" role="table" aria-label="Websites">
      <div className={cn(cols, "hidden h-[40px] border-b border-line text-[9.5px] font-medium tracking-[0.06em] text-muted uppercase")} role="row">
        {["Website", "Application", "Region", "Storage", "SSL", "Status", "Actions"].map((h, i) => <span key={h} role="columnheader" className={i ? "pl-[17px]" : undefined}>{h}</span>)}
      </div>
      <ul className="divide-y divide-line" role="rowgroup">
        {rows.map((w) => {
          return (
            <li key={w.id} role="row" aria-label={w.primaryDomain} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[71px] xl:gap-0 xl:py-[11px]")}>
              <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
                <SiteLogo w={w} />
                <Link to={`/websites/${w.id}`} title={w.primaryDomain} className="min-w-0 truncate text-[14px] font-bold hover:text-accent">{w.primaryDomain}</Link>
                <a href={siteUrl(w)} target="_blank" rel="noreferrer" className="-ml-[6px] shrink-0 text-muted hover:text-accent" aria-label={`Open ${w.primaryDomain}`} title="Open website"><ExternalLink size={14} /></a>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:contents">
                <Cell label="Application" className={cell}><span className="max-w-full truncate text-muted">{appLine(w)}</span></Cell>
                <Cell label="Region" className={cell}><Region w={w} /></Cell>
                <Cell label="Storage" className={cell}><span className="tabular-nums">{w.diskBytes != null ? formatBytes(w.diskBytes) : "—"}</span></Cell>
                <Cell label="SSL" className={cell}><SslPill ssl={w.ssl} /></Cell>
                <Cell label="Status" className={cell}><StatusPill status={w.status} /></Cell>
              </div>
              <div role="cell" className="flex items-center gap-[10px] xl:h-[48px] xl:gap-[8px] xl:border-l xl:border-line xl:pl-[10px]">
                <button type="button" onClick={() => onOpen(w)} className="inline-flex h-[36px] flex-1 items-center justify-center gap-[8px] rounded-lg bg-[#048d9f] px-[14px] text-[12.5px] font-medium text-white shadow-[0_2px_8px_rgb(4_141_159/0.25)] transition hover:bg-[#037d8d] xl:w-[88px] xl:flex-none xl:px-[10px]"><Settings size={15} className="shrink-0" />Manage</button>
                <a href={siteUrl(w)} target="_blank" rel="noreferrer" className="inline-flex h-[36px] flex-1 items-center justify-center gap-[8px] rounded-lg border border-line-strong bg-surface px-[14px] text-[12.5px] font-medium text-fg transition hover:bg-surface-2 xl:w-[72px] xl:flex-none xl:px-[10px]"><ExternalLink size={15} className="shrink-0" />Visit</a>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Grid card: the latest capture of the website's first screen fills the card; the top half shows it as is and the
 * bottom half is a frosted panel with the details. Without a capture, a placeholder says why there is none.
 */
function WebsiteCard({ w, onOpen }: { w: WebsiteSummary; onOpen: () => void }) {
  const qc = useQueryClient();
  const [failed, setFailed] = useState(false);
  const refresh = useMutation({
    mutationFn: () => api.websites.refreshScreenshot(w.id),
    onSuccess: () => { toast.success("Taking a new preview. It appears in about a minute."); void qc.invalidateQueries({ queryKey: ["websites"] }); },
    onError: (e) => toast.error((e as Error).message),
  });
  const pending = w.screenshotPending || refresh.isPending;
  const shot = w.screenshotAt && !failed;
  const reason = w.status !== "ACTIVE" ? "A preview is taken once the website is running."
    : failed ? "The preview could not be loaded."
    : pending ? "The first preview is being taken."
    : w.screenshotError ? "No preview: the website could not be opened for a screenshot."
    : "No preview yet.";
  return (
    <li className="group hd-card hd-lift relative isolate h-[300px] overflow-hidden">
      {shot ? (
        <img
          src={`/api/v1/websites/${w.id}/screenshot?v=${encodeURIComponent(w.screenshotAt!)}`}
          alt={`Preview of ${w.primaryDomain}`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="absolute inset-0 -z-10 h-full w-full origin-top-left object-cover object-left-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
      ) : null}
      {shot ? (
        // A soft fade behind the glass so it stays visible on websites whose page is white below the fold.
        <span className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[62%] bg-[linear-gradient(180deg,transparent,rgb(11_29_41/0.28)_45%,rgb(11_29_41/0.5))]" aria-hidden />
      ) : (
        <div className="absolute inset-0 -z-10 overflow-hidden bg-[linear-gradient(140deg,#0a8ea0_0%,#1b6fa8_55%,#2a3f7a_100%)]">
          {/* Soft colour blobs give the glass panel something to blur until the first capture exists. */}
          <span className="absolute -top-10 -left-8 h-44 w-44 rounded-full bg-[#3fd6e2]/60 blur-2xl" aria-hidden />
          <span className="absolute top-16 -right-10 h-48 w-48 rounded-full bg-[#7b8cff]/55 blur-2xl" aria-hidden />
          <span className="absolute -bottom-12 left-1/4 h-44 w-56 rounded-full bg-[#ffb86b]/45 blur-2xl" aria-hidden />
          <div className="relative flex h-1/2 flex-col items-center justify-center gap-2 px-6 text-center">
            <span className="rounded-2xl bg-white/90 p-1 shadow-sm"><SiteLogo w={w} size={40} /></span>
            <span className="text-[11.5px] font-medium text-white/90 [text-shadow:0_1px_2px_rgb(0_0_0/0.25)]">{reason}</span>
          </div>
        </div>
      )}

      {/* Top half: the preview itself, with status and menu. */}
      <Link to={`/websites/${w.id}`} className="absolute inset-x-0 top-0 h-1/2" aria-label={`Manage ${w.primaryDomain}`} />
      <div className="pointer-events-none absolute inset-x-2.5 top-2.5 flex items-start justify-between gap-2">
        <span className="pointer-events-auto rounded-full bg-surface/90 shadow-sm backdrop-blur-sm"><StatusPill status={w.status} /></span>
        <span className="pointer-events-auto rounded-lg bg-surface/90 shadow-sm backdrop-blur-sm">
          <ActionMenu label={`Actions for ${w.primaryDomain}`} actions={[
            { label: "Manage website", onSelect: onOpen },
            { label: "Open website", onSelect: () => window.open(siteUrl(w), "_blank", "noreferrer") },
            { label: "Refresh preview", hidden: w.status !== "ACTIVE", disabled: pending, onSelect: () => refresh.mutate() },
          ]} />
        </span>
      </div>
      {(shot || pending) && w.status === "ACTIVE" && (
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={pending}
          title={pending ? "A new preview is being taken" : "Take a new preview now"}
          className="absolute right-2.5 bottom-[calc(50%+8px)] inline-flex items-center gap-1.5 rounded-md bg-[#0b1d29]/65 px-2 py-[3px] text-[10.5px] font-medium text-white backdrop-blur-sm transition hover:bg-[#0b1d29]/80 disabled:cursor-default"
        >
          {pending ? <><Loader2 size={11} className="animate-spin" />Refreshing preview…</> : <>Updated {formatRelative(w.screenshotAt!)}<RefreshCw size={11} /></>}
        </button>
      )}

      {/* Bottom half: frosted glass with the details. */}
      <div className="absolute inset-x-0 bottom-0 flex h-1/2 flex-col justify-between border-t border-white/50 bg-[linear-gradient(180deg,rgb(255_255_255/0.6),rgb(255_255_255/0.42))] px-[14px] pt-[11px] pb-[12px] shadow-[inset_0_1px_0_rgb(255_255_255/0.6)] backdrop-blur-[18px] backdrop-saturate-[1.8] dark:border-white/12 dark:bg-[linear-gradient(180deg,rgb(15_31_44/0.62),rgb(15_31_44/0.45))] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.06)]">
        <div className="flex min-w-0 items-center gap-[10px]">
          <SiteLogo w={w} size={34} />
          <div className="min-w-0 flex-1 leading-tight">
            <div className="flex min-w-0 items-center gap-1.5">
              <Link to={`/websites/${w.id}`} className="truncate text-[14px] font-bold text-fg hover:text-accent">{w.primaryDomain}</Link>
              <a href={siteUrl(w)} target="_blank" rel="noreferrer" className="shrink-0 text-muted hover:text-accent" aria-label={`Open ${w.primaryDomain}`} title="Open website"><ExternalLink size={13} /></a>
            </div>
            <p className="mt-[3px] truncate text-[12px] text-muted">{appLine(w)}</p>
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-x-3 text-[12px] text-fg/85">
          <span className="min-w-0 flex-1"><Region w={w} /></span>
          <span className="shrink-0 tabular-nums text-muted" title="Storage used">{w.diskBytes != null ? formatBytes(w.diskBytes) : "—"}</span>
          <span className="shrink-0 [&>span]:h-[24px] [&>span]:px-[9px] [&>span]:text-[11.5px]"><SslPill ssl={w.ssl} /></span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onOpen} className="inline-flex h-[32px] flex-1 items-center justify-center gap-2 rounded-lg bg-[#048d9f] text-[12.5px] font-medium text-white shadow-[0_2px_8px_rgb(4_141_159/0.25)] hover:bg-[#037d8d]"><Settings size={14} />Manage</button>
          <a href={siteUrl(w)} target="_blank" rel="noreferrer" className="inline-flex h-[32px] flex-1 items-center justify-center gap-2 rounded-lg border border-line-strong bg-surface/80 text-[12.5px] font-medium hover:bg-surface"><ExternalLink size={14} />Visit</a>
        </div>
      </div>
    </li>
  );
}
